import { useState } from 'react';
import { createPortal } from 'react-dom';
import { imageSrc } from '../services/postService';

const STYLES = {
  'Verified':    { icon: '✅', title: 'Image verified',               color: 'text-green-400',   box: 'border-green-500/30 bg-green-500/10' },
  'Likely Real': { icon: '🟢', title: 'Likely a real photo',          color: 'text-emerald-400', box: 'border-emerald-500/30 bg-emerald-500/10' },
  'Warning':     { icon: '🚩', title: 'Warning',                      color: 'text-red-400',     box: 'border-red-500/30 bg-red-500/10' },
  'Unverified':  { icon: '⚠️', title: 'Could not verify this image',  color: 'text-yellow-400',  box: 'border-yellow-500/30 bg-yellow-500/10' },
};

function EvidenceImage({ post }) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!post.imageUrl) return null;

  const verdict = post.imageVerdict || 'Unverified';
  const style = STYLES[verdict] || STYLES.Unverified;
  const notes = post.imageNotes
    ? post.imageNotes.split(' | ')
    : ['Uploaded before automatic image checks were added'];
  const src = imageSrc(post.imageUrl);

  return (
    <>
      <div className="mt-3 flex items-start gap-3">

        {/* small thumbnail */}
        {failed ? (
          <div className="w-28 h-20 shrink-0 rounded-xl border border-dashed border-white/20 flex items-center justify-center text-center text-[10px] text-gray-400 p-1">
            Image file could not be loaded
          </div>
        ) : (
          <img
            src={src}
            alt="Evidence"
            onClick={() => setOpen(true)}
            onError={() => setFailed(true)}
            className="w-28 h-20 shrink-0 object-cover rounded-xl border border-white/10 cursor-pointer hover:opacity-80 transition"
          />
        )}

        {/* verdict + reasons */}
        <div className={`flex-1 rounded-xl border p-2 ${style.box}`}>
          <p className={`text-xs font-bold ${style.color}`}>{style.icon} {style.title}</p>
          <ul className="text-gray-300 text-xs mt-1 space-y-0.5">
            {notes.map((n, i) => <li key={i}>• {n}</li>)}
          </ul>
          {!failed && (
            <button
              onClick={() => setOpen(true)}
              className="text-cyan-400 text-xs font-semibold mt-1 hover:text-cyan-300 transition"
            >
              🖼️ View image
            </button>
          )}
        </div>
      </div>

      {/* full-size viewer (portal = not affected by the card's hover zoom) */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <img
            src={src}
            alt="Evidence full size"
            className="max-h-[70vh] max-w-full object-contain rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          <div
            className={`mt-4 max-w-xl w-full rounded-xl border p-3 ${style.box}`}
            onClick={(e) => e.stopPropagation()}
          >
            <p className={`text-sm font-bold ${style.color}`}>{style.icon} {style.title}</p>
            <ul className="text-gray-200 text-xs mt-1 space-y-0.5">
              {notes.map((n, i) => <li key={i}>• {n}</li>)}
            </ul>
            <p className="text-gray-500 text-[11px] mt-2">
              Clarity: {post.imageClarity || 'Unchecked'} · Sharpness score: {post.imageSharpness ?? '-'} · Source: {post.imageSource === 'camera' ? 'live camera' : 'uploaded file'}
            </p>
            <p className="text-gray-500 text-[11px] mt-1">
              These automatic checks reduce fake photos but cannot prove a photo is genuine. Community confirmation still decides.
            </p>
          </div>

          <button
            onClick={() => setOpen(false)}
            className="mt-4 bg-white/10 px-6 py-2 rounded-full text-sm hover:bg-white/20 transition"
          >
            Close
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

export default EvidenceImage;